/**
 * QR Code API Service
 * Migrated from 3in1cms legacy/qrApi.ts
 */

import { request } from "../core/request";
import {
  API_RESPONSE_CODES,
  BRAND,
  OPERATION_IDS,
  REST_CONNECTION_FROM,
  RURAL,
  V1,
  V3,
} from "./constants";
import { logErrorToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import type {
  ApiVariation,
  IOneOfferResponse,
  IRawApiResponseOneOffer,
  IRewardsVouchers,
  TOfferDealer,
} from "../types/qrTypes";

export interface IGetNearByStoreReqData {
  latitude: string;
  longitude: string;
  [key: string]: unknown;
}

export interface IGetNearByStoreData {
  statusCode: string;
  [key: string]: unknown;
}

/**
 * Get rewards vouchers
 * @param channel - Channel identifier (default: 'webqr')
 * @returns Rewards vouchers response
 */
export const rewardsVouchersApi = async (channel?: string): Promise<IRewardsVouchers> => {
  try {
    const response = (await request({
      operationId: OPERATION_IDS.rewardsVouchers,
      requestData: {
        channel: channel || "webqr",
      },
      connectionFrom: REST_CONNECTION_FROM,
    })) as IRewardsVouchers;

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_200) {
      throw new Error(JSON.stringify(response));
    }
    return response;
  } catch (error) {
    console.error("Error in rewardsVouchersApi", error);
    return {};
  }
};

/**
 * Get nearby store data
 * @param data - Request data with latitude/longitude
 * @returns Nearby store data
 */
export const getNearByStoreData = async (
  data: IGetNearByStoreReqData
): Promise<IGetNearByStoreData> => {
  try {
    const categoryCode = "BFLMER";
    const response = (await request({
      operationId: OPERATION_IDS.getNearbyStores,
      requestData: { ...data, categoryCode },
    })) as IGetNearByStoreData;

    if (response.statusCode !== "7001") {
      throw new Error(JSON.stringify(response));
    }
    return response;
  } catch (error) {
    logErrorToNewRelic(new Error("Error in GetDirection API"), { error: error as string });
    throw error;
  }
};

/**
 * Get one offer for QR
 * @param mobileNo - Mobile number
 * @param dealerId - Dealer ID
 * @param variation - API variation ('rural' or other)
 * @returns One offer response or null
 */
export const getOneOfferQR = async (
  mobileNo: string,
  dealerId: string,
  variation: ApiVariation
): Promise<IOneOfferResponse | null> => {
  const id = dealerId.replace(/\D/g, "");
  try {
    const response = (await request({
      operationId:
        variation === "rural" ? OPERATION_IDS.ruraldealeroffer : OPERATION_IDS.offersDealer,
      requestData: {
        mobileNumber: mobileNo,
        dealerIds: [id],
        channelFlag: "QR",
      },
      additionalHeaders: {
        apiVersion: variation === "rural" ? "v1" : "v3",
      },
    })) as IOneOfferResponse;

    if (response.statusCode !== "7001") {
      throw new Error(JSON.stringify(response));
    }
    return response;
  } catch (error) {
    logErrorToNewRelic(new Error("Error in OneOfferAPI"), { error: error as string });
    throw error;
  }
};

/**
 * Get one offers (multiple sources)
 * @param mobileNo - Mobile number
 * @param dealerId - Dealer ID
 * @param variation - API variation (RURAL or other)
 * @returns Array of offer dealers or undefined
 */
export const getOneOffers = async (
  mobileNo: string,
  dealerId: string,
  variation: ApiVariation
): Promise<TOfferDealer[] | undefined> => {
  const id = dealerId.replace(/\D/g, "");
  try {
    const response = (await request({
      operationId:
        variation === RURAL ? OPERATION_IDS.ruraldealeroffer : OPERATION_IDS.offersDealer,
      requestData: {
        mobileNumber: mobileNo,
        dealerIds: [id],
        channelFlag: BRAND,
      },
      additionalHeaders: {
        apiVersion: variation === RURAL ? V1 : V3,
      },
    })) as IRawApiResponseOneOffer;

    if (
      response.statusCode === API_RESPONSE_CODES.STATUS_CODE_7001 &&
      response.description === "Success"
    ) {
      const offerDealerArray: TOfferDealer[] = [
        ...(response.dealerOffer || []),
        ...(response?.custDealerOffers || []),
        ...(response?.custOffer || []),
      ];
      return offerDealerArray;
    }
    return [];
  } catch (error) {
    console.error("Error in getOneOffers", error);
    throw error;
  }
};
